import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Mic, Pause, Play, Square, X, Trash2, Upload, RotateCcw, Edit3, Check, Sparkles } from "lucide-react";
import { api } from "../lib/api";

// Pick the best MediaRecorder mime for the current browser. Chrome / Firefox
// produce audio/webm (opus); Safari produces audio/mp4 (aac). ffmpeg.wasm on
// the client decodes both during the MP4 export, so we don't care which one.
const pickMime = () => {
  if (typeof MediaRecorder === "undefined") return null;
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4;codecs=mp4a.40.2",
    "audio/mp4",
    "audio/ogg;codecs=opus",
  ];
  for (const m of candidates) {
    try { if (MediaRecorder.isTypeSupported(m)) return m; } catch { /* ignore */ }
  }
  return "";
};

const fmt = (sec) => {
  const s = Math.floor(sec % 60).toString().padStart(2, "0");
  const m = Math.floor(sec / 60).toString().padStart(2, "0");
  return `${m}:${s}`;
};

const NarrationRecorder = ({ open, reelId, mode = "overlay", onClose, onUploaded }) => {
  const [script, setScript] = useState("");
  const [editingScript, setEditingScript] = useState(false);
  const [draftScript, setDraftScript] = useState("");
  const [savingScript, setSavingScript] = useState(false);
  const [regenScript, setRegenScript] = useState(false);
  const [loadingScript, setLoadingScript] = useState(false);
  const [recState, setRecState] = useState("idle"); // idle | recording | paused | stopped
  const [elapsed, setElapsed] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [fontPx, setFontPx] = useState(28);

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const startTsRef = useRef(0);
  const pauseAccumRef = useRef(0);
  const pauseStartRef = useRef(0);
  const tickRef = useRef(null);
  const mimeRef = useRef("");

  // Fetch (or auto-generate) the narration script when the modal opens.
  useEffect(() => {
    if (!open || !reelId) return;
    let cancelled = false;
    setLoadingScript(true);
    api.get(`/reels/${reelId}/narration/script`)
      .then((r) => { if (!cancelled) setScript((r.data?.script || "").trim()); })
      .catch((err) => {
        if (!cancelled) {
          toast.error(err?.response?.data?.detail || "Impossibile caricare il testo");
          setScript("");
        }
      })
      .finally(() => { if (!cancelled) setLoadingScript(false); });
    return () => { cancelled = true; };
  }, [open, reelId]);

  // Cleanup on close / unmount
  useEffect(() => {
    if (!open) {
      cleanupStreamAndTimer();
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      setAudioUrl("");
      setAudioBlob(null);
      setElapsed(0);
      setRecState("idle");
      pauseAccumRef.current = 0;
    }
    return () => cleanupStreamAndTimer();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const cleanupStreamAndTimer = () => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
    try { mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive" && mediaRecorderRef.current.stop(); } catch { /* ignore */ }
    if (streamRef.current) {
      try { streamRef.current.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
      streamRef.current = null;
    }
    mediaRecorderRef.current = null;
  };

  const startTick = () => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
    }
    tickRef.current = setInterval(() => {
      const now = performance.now();
      const ms = now - startTsRef.current - pauseAccumRef.current;
      setElapsed(Math.max(0, ms / 1000));
    }, 200);
  };

  const onStart = async () => {
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      toast.error("Il tuo browser non supporta la registrazione audio");
      return;
    }
    if (typeof MediaRecorder === "undefined") {
      toast.error("Registrazione non supportata da questo browser");
      return;
    }
    try {
      // Discard any previous take
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      setAudioUrl("");
      setAudioBlob(null);
      setElapsed(0);
      pauseAccumRef.current = 0;

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      streamRef.current = stream;
      const mime = pickMime();
      mimeRef.current = mime || "";
      const opts = mime ? { mimeType: mime } : undefined;
      const rec = new MediaRecorder(stream, opts);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blobMime = mimeRef.current || (chunksRef.current[0]?.type) || "audio/webm";
        const blob = new Blob(chunksRef.current, { type: blobMime });
        setAudioBlob(blob);
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        setRecState("stopped");
        if (streamRef.current) {
          try { streamRef.current.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
          streamRef.current = null;
        }
        if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
      };
      mediaRecorderRef.current = rec;
      rec.start(250);
      startTsRef.current = performance.now();
      setRecState("recording");
      startTick();
    } catch (err) {
      toast.error(err?.message?.includes("Permission") || err?.name === "NotAllowedError"
        ? "Permesso microfono negato — abilita il microfono nel browser"
        : (err?.message || "Errore avvio registrazione"));
    }
  };

  const onPause = () => {
    const rec = mediaRecorderRef.current;
    if (!rec || rec.state !== "recording") return;
    try {
      rec.pause();
      pauseStartRef.current = performance.now();
      setRecState("paused");
      if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    } catch { /* ignore */ }
  };

  const onResume = () => {
    const rec = mediaRecorderRef.current;
    if (!rec || rec.state !== "paused") return;
    try {
      rec.resume();
      pauseAccumRef.current += performance.now() - pauseStartRef.current;
      setRecState("recording");
      startTick();
    } catch { /* ignore */ }
  };

  const onStop = () => {
    const rec = mediaRecorderRef.current;
    if (!rec) return;
    try { rec.state !== "inactive" && rec.stop(); } catch { /* ignore */ }
  };

  const onRetake = () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioUrl("");
    setAudioBlob(null);
    setElapsed(0);
    pauseAccumRef.current = 0;
    setRecState("idle");
  };

  const onSaveScript = async () => {
    const text = (draftScript || "").trim();
    if (!text) {
      toast.error("Il testo non può essere vuoto");
      return;
    }
    if (text.length > 4000) {
      toast.error("Testo troppo lungo (max 4000 caratteri)");
      return;
    }
    setSavingScript(true);
    try {
      const { data } = await api.put(`/reels/${reelId}/narration/script`, { script: text });
      setScript((data?.script || text).trim());
      setEditingScript(false);
      setDraftScript("");
      toast.success("Testo aggiornato");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore salvataggio testo");
    } finally {
      setSavingScript(false);
    }
  };

  const onRegenerate = async () => {
    if (regenScript) return;
    setRegenScript(true);
    try {
      const { data } = await api.post(`/reels/${reelId}/narration/script/regen`, {});
      setScript((data?.script || "").trim());
      toast.success("Testo rigenerato dall'AI");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore rigenerazione");
    } finally {
      setRegenScript(false);
    }
  };

  const onUpload = async () => {
    setUploading(true);
    try {
      const ext = (audioBlob.type || "").includes("mp4") ? "m4a"
                : (audioBlob.type || "").includes("ogg") ? "ogg"
                : (audioBlob.type || "").includes("mpeg") ? "mp3"
                : "webm";
      const file = new File([audioBlob], `voce-${Date.now()}.${ext}`, { type: audioBlob.type || "audio/webm" });
      const form = new FormData();
      form.append("audio", file);
      // User recordings always use respect_videos mode — the voice must
      // pause on every user video so its original soundtrack plays
      // through. The teleprompter "mode" prop is therefore ignored on
      // the upload side.
      form.append("mode", "respect_videos");
      // Send the recorded wall-clock duration so the backend can sync
      // every scene's display time to the voice length.
      form.append("audio_duration_ms", String(Math.max(0, Math.round(elapsed * 1000))));
      form.append("sync_scenes", "true");
      const { data } = await api.post(`/reels/${reelId}/narration/upload-audio`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      const synced = data?.synced_scenes || 0;
      const perScene = data?.per_image_scene_ms || 0;
      if (synced > 0 && perScene > 0) {
        toast.success(`Voce salvata! ${synced} scene sincronizzate a ${(perScene / 1000).toFixed(1)}s ciascuna`);
      } else {
        toast.success("Voce registrata salvata!");
      }
      if (onUploaded) onUploaded(data);
      onClose && onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore caricamento audio");
    } finally {
      setUploading(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[110] bg-black/95 backdrop-blur-sm flex flex-col"
      data-testid="narration-recorder-modal"
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800 flex-shrink-0">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF4400]">
            · Registra la tua voce
          </div>
          <div className="font-display font-black text-white text-lg md:text-xl tracking-tighter">
            Leggi il testo · Registra · Carica
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Font size controls */}
          <button
            onClick={() => setFontPx((s) => Math.max(18, s - 4))}
            className="w-9 h-9 rounded-sm bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-600 font-display font-bold text-sm"
            title="Riduci testo"
            data-testid="narration-font-smaller"
          >
            A−
          </button>
          <button
            onClick={() => setFontPx((s) => Math.min(56, s + 4))}
            className="w-9 h-9 rounded-sm bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-600 font-display font-bold text-base"
            title="Ingrandisci testo"
            data-testid="narration-font-bigger"
          >
            A+
          </button>
          {/* Edit script: only available when not recording. Lets the
              user tweak the AI-generated narration before reading it. */}
          {recState === "idle" && !editingScript && (
            <button
              onClick={() => { setDraftScript(script); setEditingScript(true); }}
              disabled={loadingScript || !script}
              className="w-9 h-9 rounded-sm bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-white hover:border-[#FF4400] flex items-center justify-center disabled:opacity-30"
              title="Modifica testo"
              data-testid="narration-script-edit"
            >
              <Edit3 className="w-4 h-4" />
            </button>
          )}
          {/* Regenerate: ask the LLM for a fresh take. Useful if the user
              hates the tone of the auto-generated script. */}
          {recState === "idle" && !editingScript && (
            <button
              onClick={onRegenerate}
              disabled={loadingScript || regenScript}
              className="w-9 h-9 rounded-sm bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-white hover:border-[#FF4400] flex items-center justify-center disabled:opacity-30"
              title="Rigenera testo con AI"
              data-testid="narration-script-regen"
            >
              {regenScript ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            </button>
          )}
          <button
            onClick={() => { if (recState === "recording" || recState === "paused") onStop(); onClose && onClose(); }}
            className="w-9 h-9 rounded-sm bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-white hover:border-red-600 flex items-center justify-center"
            title="Chiudi"
            data-testid="narration-recorder-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Teleprompter body */}
      <div className="flex-1 overflow-y-auto px-5 md:px-12 py-8">
        <div className="max-w-3xl mx-auto">
          {loadingScript ? (
            <div className="flex items-center gap-3 text-zinc-400 font-mono text-sm">
              <Loader2 className="w-5 h-5 animate-spin" /> Generazione testo in corso...
            </div>
          ) : editingScript ? (
            <div className="space-y-3">
              <textarea
                value={draftScript}
                onChange={(e) => setDraftScript(e.target.value)}
                autoFocus
                placeholder="Modifica il testo che leggerai..."
                style={{ fontSize: `${fontPx}px`, lineHeight: 1.45, minHeight: "55vh" }}
                className="w-full bg-zinc-950 border border-zinc-700 rounded-sm px-4 py-3 text-white font-body leading-relaxed focus:border-[#FF4400] focus:outline-none resize-y"
                data-testid="narration-script-textarea"
              />
              <div className="flex items-center gap-2 justify-end">
                <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-600 mr-auto">
                  {draftScript.length} / 4000 caratteri
                </span>
                <button
                  onClick={() => { setEditingScript(false); setDraftScript(""); }}
                  disabled={savingScript}
                  className="px-4 py-2 border border-zinc-800 text-zinc-300 hover:text-white hover:border-zinc-600 font-display font-bold text-xs uppercase tracking-widest rounded-sm transition-colors disabled:opacity-40"
                  data-testid="narration-script-edit-cancel"
                >
                  Annulla
                </button>
                <button
                  onClick={onSaveScript}
                  disabled={savingScript || !draftScript.trim() || draftScript.length > 4000}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#FF4400] hover:bg-[#FF6633] disabled:opacity-40 text-white font-display font-bold text-xs uppercase tracking-widest rounded-sm transition-colors"
                  data-testid="narration-script-edit-save"
                >
                  {savingScript ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                  Salva testo
                </button>
              </div>
            </div>
          ) : script ? (
            <p
              className="text-white font-body leading-relaxed whitespace-pre-wrap"
              style={{ fontSize: `${fontPx}px`, lineHeight: 1.45 }}
              data-testid="narration-script-text"
            >
              {script}
            </p>
          ) : (
            <p className="text-zinc-500 font-body text-sm">
              Nessun testo disponibile. Chiudi e riprova.
            </p>
          )}
        </div>
      </div>

      {/* Bottom controls */}
      <div className="border-t border-zinc-800 bg-black px-5 py-5 flex-shrink-0">
        <div className="max-w-3xl mx-auto flex flex-col items-center gap-4">
          {/* Status / timer */}
          <div className="flex items-center gap-3">
            {recState === "recording" && (
              <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
            )}
            <span className="font-mono text-sm text-zinc-300 tracking-widest" data-testid="narration-rec-timer">
              {fmt(elapsed)}
            </span>
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-600">
              {recState === "idle" && "pronto"}
              {recState === "recording" && "registrazione"}
              {recState === "paused" && "in pausa"}
              {recState === "stopped" && "registrazione completata"}
            </span>
          </div>

          {/* Preview (after stop) */}
          {recState === "stopped" && audioUrl && (
            <audio src={audioUrl} controls className="w-full max-w-md" data-testid="narration-rec-preview" />
          )}

          {/* Action buttons */}
          <div className="flex items-center gap-3 flex-wrap justify-center">
            {recState === "idle" && (
              <button
                onClick={onStart}
                disabled={loadingScript || !script}
                data-testid="narration-rec-start"
                className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-display font-bold rounded-sm transition-colors"
              >
                <Mic className="w-5 h-5" />
                Inizia registrazione
              </button>
            )}
            {recState === "recording" && (
              <>
                <button
                  onClick={onPause}
                  data-testid="narration-rec-pause"
                  className="inline-flex items-center gap-2 px-5 py-3 bg-zinc-800 hover:bg-zinc-700 text-white font-display font-bold rounded-sm transition-colors"
                >
                  <Pause className="w-5 h-5" /> Pausa
                </button>
                <button
                  onClick={onStop}
                  data-testid="narration-rec-stop"
                  className="inline-flex items-center gap-2 px-5 py-3 bg-[#FF4400] hover:bg-[#FF6633] text-white font-display font-bold rounded-sm transition-colors"
                >
                  <Square className="w-5 h-5" /> Stop
                </button>
              </>
            )}
            {recState === "paused" && (
              <>
                <button
                  onClick={onResume}
                  data-testid="narration-rec-resume"
                  className="inline-flex items-center gap-2 px-5 py-3 bg-red-600 hover:bg-red-500 text-white font-display font-bold rounded-sm transition-colors"
                >
                  <Play className="w-5 h-5" /> Riprendi
                </button>
                <button
                  onClick={onStop}
                  data-testid="narration-rec-stop"
                  className="inline-flex items-center gap-2 px-5 py-3 bg-[#FF4400] hover:bg-[#FF6633] text-white font-display font-bold rounded-sm transition-colors"
                >
                  <Square className="w-5 h-5" /> Stop
                </button>
              </>
            )}
            {recState === "stopped" && (
              <>
                <button
                  onClick={onRetake}
                  disabled={uploading}
                  data-testid="narration-rec-retake"
                  className="inline-flex items-center gap-2 px-5 py-3 border border-zinc-700 hover:border-zinc-500 text-zinc-300 hover:text-white font-display font-bold rounded-sm transition-colors disabled:opacity-40"
                >
                  <RotateCcw className="w-5 h-5" /> Rifai
                </button>
                <button
                  onClick={onUpload}
                  disabled={uploading || !audioBlob}
                  data-testid="narration-rec-upload"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF4400] hover:bg-[#FF6633] disabled:opacity-40 text-white font-display font-bold rounded-sm transition-colors"
                >
                  {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
                  {uploading ? "Caricamento..." : "Usa questa voce"}
                </button>
              </>
            )}
          </div>

          <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-600 text-center max-w-md">
            · Tieni il telefono vicino · Parla naturale · La durata delle scene verrà sincronizzata automaticamente con la tua voce
          </p>
        </div>
      </div>
    </div>
  );
};

// Future-proof placeholder export; keep empty to satisfy linter.
NarrationRecorder._icons = {};

export default NarrationRecorder;
